import { Box, IconButton } from "@mui/material";
import React, { useCallback, useEffect, useMemo } from "react";
// @ts-ignore
import { StriimTypography } from "@striim/striim-ui";
import StreamPreviewTable from "./stream-preview-table";
import { useAppSelector, useAppDispatch } from "src/stores/redux/hooks";
import {
    getIsStreamPreviewVisible,
    getIsStreamGraphPreviewVisible,
    getIsStreamPreviewExpanded
} from "../../helpers/selectors";
import {
    setStreamPreviewVisibleAction,
    clearStreamPreviewDataAction,
    setStreamGraphPreviewVisibleAction,
    setStreamPreviewExpandedAction
} from "src/stores/redux/features/flowdesigner/flowdesigner.slice";
import streamDataLoader from "../stream-data-loader";
import ForeseerGraph from "../foreseer-graph/foreseer-graph";
import { Expand, Minimize, Close, Refresh } from "src/generic/icon/customIcons";
import metaobjectConverter from "core/services/metaStoreService/metaobject-converter";
import statusManagement from "src/status-management";
import { useAppStatus } from "../../helpers/flow.hooks";

const COLLAPSED_HEIGHT = "300px";
const EXPANDED_HEIGHT = "calc(100vh - 104px)"; // full height minus topbar and progress bar

const getStyles = (isExpanded: boolean) => ({
    panel: {
        position: "absolute" as const,
        bottom: 0,
        left: 0,
        right: 0,
        height: isExpanded ? EXPANDED_HEIGHT : COLLAPSED_HEIGHT,
        backgroundColor: "#fff",
        borderTop: "1px solid #e0e0e0",
        boxShadow: "0px -2px 8px rgba(0,0,0,0.1)",
        zIndex: 7,
        display: "flex",
        flexDirection: "column" as const,
        transition: "height 0.3s ease",
        "& .table-styles": { borderRadius: "0px" }
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 16px",
        borderBottom: "1px solid #e0e0e0",
        flexShrink: 0
    },
    headerActions: {
        display: "flex",
        alignItems: "center",
        gap: "4px"
    },
    content: {
        flex: 1,
        overflow: "auto",
        width: "100%"
    },
    iconButton: {
        width: 28,
        height: 28,
        "& svg": {
            width: 16,
            height: 16
        }
    }
});

const StreamPreviewContainer: React.FC = () => {
    const dispatch = useAppDispatch();
    const isVisible = useAppSelector(getIsStreamPreviewVisible);
    const isGraphVisible = useAppSelector(getIsStreamGraphPreviewVisible);
    const isExpanded = useAppSelector(getIsStreamPreviewExpanded);
    const appStatus = useAppStatus();

    const handleClose = useCallback(() => {
        streamDataLoader.stop();
        dispatch(setStreamPreviewVisibleAction(false));
        dispatch(setStreamGraphPreviewVisibleAction(false));
        dispatch(clearStreamPreviewDataAction());
    }, [dispatch]);

    const handleRefresh = useCallback(() => {
        const streamName = streamDataLoader.streamName;
        streamDataLoader.stop();
        dispatch(clearStreamPreviewDataAction());
        streamDataLoader.setStreamName(streamName);
        streamDataLoader.start();
    }, [dispatch]);

    const handleToggleExpand = useCallback(() => {
        dispatch(setStreamPreviewExpandedAction(!isExpanded));
    }, [dispatch, isExpanded]);

    const title = useMemo(() => {
        const name = streamDataLoader.previewSourceName || metaobjectConverter.getName(streamDataLoader.streamName);
        return `Preview data ${name}`;
    }, [streamDataLoader.streamName, streamDataLoader.previewSourceName]);
    const styles = getStyles(isExpanded);

    useEffect(() => {
        return () => {
            dispatch(setStreamPreviewVisibleAction(false));
            dispatch(setStreamGraphPreviewVisibleAction(false));
            dispatch(clearStreamPreviewDataAction());
            streamDataLoader.stop();
        };
    }, [dispatch]);

    if (!isVisible || (appStatus && statusManagement.isApplicationCreated(appStatus))) return null;

    return (
        <Box sx={styles.panel}>
            <Box sx={styles.header}>
                <StriimTypography data-test-id="preview-data-title" variant="h3">
                    {title}
                </StriimTypography>
                <Box sx={styles.headerActions}>
                    <IconButton onClick={handleRefresh} size="small" sx={styles.iconButton} title="Refresh">
                        <Refresh />
                    </IconButton>
                    <IconButton
                        onClick={handleToggleExpand}
                        size="small"
                        sx={styles.iconButton}
                        title={isExpanded ? "Collapse" : "Expand"}
                    >
                        {isExpanded ? <Minimize /> : <Expand />}
                    </IconButton>
                    <IconButton onClick={handleClose} size="small" sx={styles.iconButton} title="Close">
                        <Close />
                    </IconButton>
                </Box>
            </Box>
            <Box sx={styles.content}>
                {isGraphVisible ? <ForeseerGraph /> : <StreamPreviewTable isVisible={isVisible} />}
            </Box>
        </Box>
    );
};

export default StreamPreviewContainer;
